import BasicGridToolbar from '@components/_shared/BasicGridToolbar';
import generateArchiveColumns from '@components/Home/Historian/Archive/ArchiveGridColumns';
import UseArchivistPagination from '@hooks/api/queries/archivist/useArchivistGetPagination';
import {
  DataGridPro,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridPaginationModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import QueryKeys from '@src/constants/queryKeys';
import {
  ArchivePoint,
  ArchivistPaginationResponse,
  archiveFields,
  filterObject,
} from '@src/types/Archivist';
import { PaginationModel } from '@src/types/pagination/PaginationModel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const ArchivePointsTable: React.FC = () => {
  const [_requestBodyDetails, setRequestBodyDetails] = useState<Record<
    string,
    string
  > | null>(null);

  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [filterParams, setFilterParams] = useState<{
    filters?: filterObject[];
  }>({});

  const queryParams = useMemo(
    () => ({
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
      ...filterParams,
    }),
    [paginationModel.page, paginationModel.pageSize, filterParams],
  );

  const queryOptions = useMemo(
    () => ({
      enabled: true,
      queryKeys: [
        QueryKeys.getPagination,
        paginationModel.page.toString(),
        paginationModel.pageSize.toString(),
        JSON.stringify(filterParams.filters || []),
      ],
    }),
    [paginationModel.page, paginationModel.pageSize, filterParams],
  );

  const { data, isLoading } = UseArchivistPagination({
    params: queryParams,
    options: queryOptions,
  }) as {
    data: ArchivistPaginationResponse | undefined;
    isLoading: boolean;
    refetch: () => void;
  };

  const [archiveRows, setRows] = useState<ArchivePoint[]>([]);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);

  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      setRows(data.data);
      setTotalRowCount(data.total_count || 0);
    } else if (data && !data.data) {
      setRows([]);
      setTotalRowCount(0);
    }
  }, [data]);

  const handleDetailsClick = (gridRow: GridValidRowModel) => (): void => {
    setRequestBodyDetails(gridRow.body);
  };

  const archiveColumns: GridColDef[] =
    generateArchiveColumns(handleDetailsClick);

  const onFilterChange = useCallback((filterModel: GridFilterModel): void => {
    const filters: GridFilterItem[] = filterModel.items;
    const filtersOn: boolean = filters.length > 0;

    if (
      filtersOn &&
      filters.some(
        (filter) => filter.value !== undefined && filter.value !== '',
      )
    ) {
      const filterObjects: filterObject[] = [];

      filterModel.items.forEach((filterItem: GridFilterItem): void => {
        if (filterItem.value !== undefined && filterItem.value !== '') {
          switch (filterItem.field) {
            case archiveFields.topic:
              filterObjects.push({ field: 'topic', value: filterItem.value });
              break;
            case archiveFields.siteId:
              filterObjects.push({ field: 'siteId', value: filterItem.value });
              break;
            case archiveFields.parentDeviceType:
              filterObjects.push({
                field: 'parentDeviceType',
                value: filterItem.value,
              });
              break;
            case archiveFields.parentDeviceId:
              filterObjects.push({
                field: 'parentDeviceId',
                value: filterItem.value,
              });
              break;
            case archiveFields.deviceType:
              filterObjects.push({
                field: 'deviceType',
                value: filterItem.value,
              });
              break;
            case archiveFields.deviceId:
              filterObjects.push({
                field: 'deviceId',
                value: filterItem.value,
              });
              break;
            case archiveFields.pointName:
              filterObjects.push({
                field: 'pointName',
                value: filterItem.value,
              });
              break;
            case archiveFields.dataType:
              filterObjects.push({
                field: 'dataType',
                value: filterItem.value,
              });
              break;
            default:
              break;
          }
        }
      });

      setFilterParams((prev) => ({
        ...prev,
        filters: filterObjects,
      }));

      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    } else {
      setFilterParams((prev) => ({
        ...prev,
        filters: undefined,
      }));
    }
  }, []);

  const handlePaginationChange = (
    paginationEvent: GridPaginationModel,
  ): void => {
    const newPaginationModel = {
      page: paginationEvent.page,
      pageSize: paginationEvent.pageSize,
    };
    setPaginationModel(newPaginationModel);
  };

  const getRowId = useMemo(
    () => (row: ArchivePoint) => {
      return row?._id?.$oid;
    },
    [],
  );

  return (
    <div className='home-row frow'>
      <div className='fcol h100 full-width'>
        <div className='cell block-container flex-1 overflow-y-auto'>
          <DataGridPro
            density='compact'
            pageSizeOptions={[10, 25, 50, 100]}
            pagination
            rowCount={totalRowCount}
            paginationMode='server'
            columns={archiveColumns}
            rows={archiveRows}
            loading={isLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            getRowId={getRowId}
            filterMode='server'
            onFilterModelChange={onFilterChange}
            slots={{ toolbar: BasicGridToolbar }}
          />
        </div>
      </div>
    </div>
  );
};

export default ArchivePointsTable;
